import * as React from 'react'
import CHANNELS, { channelById } from '../../../../../../constants/Channels'
import EbayRelistDialog from '../../../../../listing_manager/ebay/RelistDialog'
import GoodwillfindsRelistDialog from '../../../../../listing_manager/goodwillfinds/RelistDialog'
import DeleteDialog from '../../../../../listing_manager/shared/DeleteDialog'
import DelistDialog from '../../../../../listing_manager/shared/DelistDialog'
import ListDialog from '../../../../../listing_manager/shared/ListDialog'
import PurgeDialog from '../../../../../listing_manager/shared/PurgeDialog'
import ShopgoodwillPurgeDialog from '../../../../../listing_manager/shopgoodwill/PurgeDialog'
import ShopgoodwillRelistDialog from '../../../../../listing_manager/shopgoodwill/RelistDialog'
import BulkListDialog from '../../../../../listing_manager/shopify/BulkListDialog'
import { ListingAction, ListingActionDialogType } from '../../types'
import AddAdditionalDetailsDialog from './AddAdditionalDetailsDialog'
import { ProductV4 } from '../../../../../../types/api'
import RelistDialog from '../../../../../listing_manager/shared/RelistDialog'
import Client from '../../../../../../client'

interface Props extends ListingActionDialogType {
  onClose: (changePerformed?: boolean) => void
  product: ProductV4
  reloadProduct: () => void
  productRecycled?: boolean
  marketAccountId?: number // only necessary for shopify to load in the proper defaults
  actionType?: ListingAction
}

const ListingActionDialog: React.FC<Props> = props => {
  const { actionType, listing } = props

  if (!actionType || !listing) return null

  const channel = channelById(listing.marketId)

  // shared
  if (actionType === 'list') {
    return (
      <ListDialog
        isOpen
        onClose={props.onClose}
        data={[listing.listingId]}
        listEndpoint={channel.listEndpoint}
        marketName={channel.humanReadableName}
      />
    )
  }

  if (actionType === 'delist') {
    return (
      <DelistDialog
        isOpen
        onClose={props.onClose}
        data={[listing.listingId]}
        title="End early"
        warningMessage={
          (listing.bidCount &&
            listing.bidCount > 1 &&
            `Caution, this item may still have bids. If you haven't already, please cancel those bids directly on ${channel.humanReadableName} before proceeding.`) ||
          null
        }
        endListingEndpoint={channel.delistEndpoint}
      />
    )
  }

  if (actionType === 'delete') {
    return (
      <DeleteDialog
        channelName={channel.name}
        listingId={listing.listingId}
        isOpen
        onClose={success => (success ? props.reloadProduct() : props.onClose())}
      />
    )
  }

  if (actionType === 'recycle') {
    if (channel.name === 'shopgoodwill') {
      return (
        <ShopgoodwillPurgeDialog
          isOpen
          onClose={props.onClose}
          data={[listing.listingId]}
          listingPaymentDeadline={listing.paymentDeadline}
        />
      )
    }

    return <PurgeDialog isOpen onClose={props.onClose} data={[listing.listingId]} channelId={channel.id} />
  }
  const isSgw = channel.id === CHANNELS.SHOPGOODWILL.id
  const isEbay = channel.id === CHANNELS.EBAY.id
  const addAdditionalInfoChannel = isSgw || isEbay
  if (actionType === 'addAdditionalInfo' && addAdditionalInfoChannel) {
    return (
      <AddAdditionalDetailsDialog
        listingId={listing.listingId}
        bidCount={listing.bidCount}
        channel={channel}
        onClose={props.onClose}
      />
    )
  }

  // specific channel/actions
  if (actionType === 'relist') {
    const relistProps = {
      data: [listing.listingId],
      onClose: props.onClose,
      hasSold: listing.state === 'SOLD',
      isOpen: true,
      recycledProductId: props.productRecycled ? props.product.id : null,
      onProductRestored: props.reloadProduct,
      currencyCode: listing.currencyCode
    }

    if (channel.name === 'ebay') {
      return <EbayRelistDialog marketAccountId={listing.marketAccountId} {...relistProps} />
    }

    if (channel.name === 'shopgoodwill') {
      return <ShopgoodwillRelistDialog {...relistProps} />
    }

    if (channel.name === 'shopify') {
      return (
        <BulkListDialog
          action="relist"
          aProductHasVariants={props.product.variants?.length > 0}
          marketAccountId={props.marketAccountId}
          {...relistProps}
        />
      )
    }

    if (channel.id === CHANNELS.GOODWILLFINDS.id) {
      return <GoodwillfindsRelistDialog isOpen marketAccountId={props.marketAccountId} {...relistProps} />
    }

    if (channel.name === 'etsy') {
      const { recycledProductId, ...dialogProps } = relistProps
      return (
        <RelistDialog
          {...dialogProps}
          onSubmit={({ price }) =>
            Client.bulkRelist(channel, relistProps.data, { price }).then(res => ({
              failed: res.data.failed
            }))
          }
        />
      )
    }
  }

  return null
}

export default ListingActionDialog
